import apiClient from "../api/apiClient";
import type { CountryDto, CountryResponse } from "../types/country";

const BASE = "/countries";

/** The API clamps PageSize to 100 (GetCountriesHandler.MaxPageSize). */
const PAGE_SIZE = 100;

/** Stops a bad `count` from the API turning pagination into an unbounded loop. */
const MAX_PAGES = 20;

/**
 * Fetch the full country list from the database (mirrors Blazor
 * CountryService.GetAllCountriesAsync).
 * Endpoint: GET /api/v1/countries?PageIndex={n}&PageSize=100
 *
 * The endpoint is paged and clamps PageSize to 100, so a single request silently
 * truncates once the Country table passes 100 active rows — currency pickers would
 * then be missing corridors that exist in the database. This walks every page and
 * returns the full set, using the server's own `count` to decide when to stop.
 *
 * Returns whatever was collected before a failure (or [] if the first page fails)
 * rather than throwing, matching the previous contract callers rely on.
 */
export async function getAllCountries(): Promise<CountryDto[]> {
  const all: CountryDto[] = [];

  try {
    for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
      const { data } = await apiClient.get<CountryResponse>(BASE, {
        params: { PageIndex: pageIndex, PageSize: PAGE_SIZE },
      });

      const page = data.countries?.data ?? [];
      all.push(...page);

      // Stop on a short/empty page, or once we've collected everything the
      // server says exists. `count` is the total row count, not the page size.
      const total = data.countries?.count ?? 0;
      if (page.length < PAGE_SIZE || all.length >= total) break;
    }
  } catch (err) {
    console.error("[countryService] getAllCountries error", err);
  }

  return all;
}
